/**
 * PermissionContext - Global permission caching and checking
 * 
 * Provides centralized permission management with caching to avoid
 * redundant API calls. Fetches user permissions once and caches them
 * for the entire application.
 */

import React, { createContext, useContext, useState, useEffect, useCallback, useMemo } from 'react';
import { useAuth } from './AuthContext';
import { apiJson } from '../utils/apiClient';
import { ROLE_TEMPLATES } from '../utils/permissionConstants';
import { getOwnerUserId } from '../utils/identity';
import { isClientUser } from '../app/router/helpers';
import { logger } from "../utils/logger";

const PermissionContext = createContext(null);

export const PermissionProvider = ({ children }) => {
  const { user } = useAuth();
  const [permissions, setPermissions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // eslint-disable-next-line react-hooks/exhaustive-deps -- intentionally deps on identity fields only
  const ownerUserId = useMemo(() => getOwnerUserId(user), [user && user.uuid, user && user.id, user && user.impersonates_user_id]);

  const fetchPermissions = useCallback(async (userId) => {
    if (!userId) {
      setPermissions([]);
      setLoading(false);
      return;
    }

    try {
      setLoading(true);
      setError(null);
      const data = await apiJson(`/api/permissions/user/${userId}`);
      setPermissions(data.permissions || []);
    } catch (err) {
      logger.error('Error fetching permissions (non-blocking):', err);
      setError(err);
      setPermissions([]);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    // Client portal users have no staff-level permissions; skip the
    // fetch to avoid a 403 console error.
    if (isClientUser(user)) {
      setPermissions([]);
      setLoading(false);
      return;
    }
    if (ownerUserId) {
      fetchPermissions(ownerUserId);
    } else {
      setPermissions([]);
      setLoading(false);
    }
  }, [ownerUserId, fetchPermissions, user]);

  /**
   * Check if user has a specific permission
   * @param {string} permissionKey - Permission key (e.g., "billing.view")
   * @returns {boolean}
   */
  const hasPermission = useCallback((permissionKey: string): boolean => {
    if (loading) return false;

    if (permissions.length > 0) {
      return permissions.some((p: { permission_key: string; is_granted: boolean }) => p.permission_key === permissionKey && p.is_granted);
    }
    
    // Fallback to ROLE_TEMPLATES only when loading is complete and API
    // returned empty (user has no DB overrides).
    if (user === null || user === false || !user.role) return false;
    // Check primary role
    const primaryPerms = ROLE_TEMPLATES[user.role as keyof typeof ROLE_TEMPLATES];
    if (primaryPerms && (primaryPerms as Record<string, boolean>)[permissionKey]) return true;
    // Check additional roles (UserRole many-to-many)
    const additionalRoles = (user as Record<string, unknown>).additional_roles;
    if (Array.isArray(additionalRoles)) {
      for (const role of additionalRoles) {
        const rolePerms = ROLE_TEMPLATES[role as keyof typeof ROLE_TEMPLATES];
        if (rolePerms && (rolePerms as Record<string, boolean>)[permissionKey]) return true;
      }
    }
    return false;
  }, [permissions, user, loading]);

  /**
   * Check if user has any of the specified permissions
   * @param {string[]} permissionKeys - Array of permission keys
   * @returns {boolean}
   */
  const hasAnyPermission = useCallback((permissionKeys) => {
    if (!Array.isArray(permissionKeys) || permissionKeys.length === 0) {
      return true;
    }
    return permissionKeys.some(key => hasPermission(key));
  }, [hasPermission]);

  /**
   * Check if user has all of the specified permissions
   * @param {string[]} permissionKeys - Array of permission keys
   * @returns {boolean}
   */
  const hasAllPermissions = useCallback((permissionKeys) => {
    if (!Array.isArray(permissionKeys) || permissionKeys.length === 0) {
      return true;
    }
    return permissionKeys.every(key => hasPermission(key));
  }, [hasPermission]);

  /**
   * Manually refresh permissions (useful after permission grants/revokes)
   */
  const refetchPermissions = useCallback(() => {
    if (ownerUserId) {
      fetchPermissions(ownerUserId);
    }
  }, [ownerUserId, fetchPermissions]);

  const value = {
    permissions,
    loading,
    error,
    hasPermission,
    hasAnyPermission,
    hasAllPermissions,
    refetchPermissions,
  };

  return (
    <PermissionContext.Provider value={value}>
      {children}
    </PermissionContext.Provider>
  );
};

/**
 * Hook to access permission context
 * @returns {Object} Permission context value
 */
export const usePermissionContext = () => {
  const context = useContext(PermissionContext);
  if (!context) {
    throw new Error('usePermissionContext must be used within a PermissionProvider');
  }
  return context;
};

export default PermissionContext;
